import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useChantiersRealtime } from "@/hooks/useChantiersRealtime";
import ChantiersSidebarPanel from "@/components/gestion-financiere/ChantiersSidebarPanel";
import { MiroAnnotations } from "@/components/annotations/MiroAnnotations";
import { Button } from "@/components/ui/button";
import { Plus, Building, Clock, CheckCircle2, AlertCircle, Info, Filter, Loader2 } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ProjectCard from "@/components/ProjectCard";
import EmptyState from "@/components/EmptyState";
import { labels, emptyStates, toasts } from "@/lib/content";
import ConfirmDialog from "@/components/ConfirmDialog";
import { ChantierFormDialog } from "@/components/chantiers/ChantierFormDialog";
import { format } from "date-fns";
import { getDateFinEffective, AffectationRetard, SuspensionInfo as SuspensionInfoDateUtils } from "@/lib/dateUtils";
import { useHighlightButton } from "@/hooks/useHighlightButton";
import { cn } from "@/lib/utils";
import { SEUILS_MARGE } from "@/lib/calculs-rentabilite";
import { X } from "lucide-react";
import { useChecklistStore } from "@/stores/checklistStore";
import { useChantierFilterStore } from "@/stores/chantierFilterStore";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { ModalSuspensionChantier } from "@/components/project/ModalSuspensionChantier";
import { ModalRepriseChantier } from "@/components/project/ModalRepriseChantier";
import { useChantierStatus } from "@/hooks/useChantierStatus";
import type { StatutChantier } from "@/constants/statuts";
import type { SuspensionInfo } from "@/types/calendar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/lib/toast-utils";

const Projects = () => {
  const { user } = useAuth();
  // toast Sonner importé depuis @/lib/toast-utils
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Gestion des limites d'abonnement
  const { canCreateChantier, checkDeletionAllowed, refreshBillingUsage, limits, billingUsage, planName, entrepriseId: subEntrepriseId } = useSubscription();

  // Détection arrivée depuis la checklist
  const fromChecklist = (location.state as any)?.fromChecklist;

  // Scroll en haut de page si on vient de la checklist
  useEffect(() => {
    if (fromChecklist) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [fromChecklist]);

  // Effet de highlight depuis la checklist (5 secondes)
  const isCreateProjectHighlighted = useHighlightButton('create-project');
  // Store pour la checklist (stocker l'ID du chantier créé depuis la checklist)
  const { setLastCreatedChantierId, setHighlightNewChantier, lastCreatedChantierId, highlightNewChantier } = useChecklistStore();
  // État local pour le highlight avec timeout de 5 secondes
  const [highlightedChantierId, setHighlightedChantierId] = useState<string | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [entrepriseId, setEntrepriseId] = useState<string | null>(null);
  const [editStateDialogOpen, setEditStateDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isDeletingChantier, setIsDeletingChantier] = useState(false);
  // États pour les modals de suspension et reprise
  const [suspensionModalOpen, setSuspensionModalOpen] = useState(false);
  const [repriseModalOpen, setRepriseModalOpen] = useState(false);
  const [selectedChantierForSuspension, setSelectedChantierForSuspension] = useState<any | null>(null);
  const [suspensionInfoForReprise, setSuspensionInfoForReprise] = useState<SuspensionInfo | null>(null);
  // État pour le modal de limite atteinte
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState("");
  // État pour le modal d'avertissement suppression
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);
  const [deleteWarningMessage, setDeleteWarningMessage] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [isDeletionFree, setIsDeletionFree] = useState(false);
  // Filtre par statut (les archivés sont masqués par défaut avec "all")
  const { filterStatus, setFilterStatus } = useChantierFilterStore();
  // Filtre URL alerte (marge critique < 10%)
  const [filterAlerte, setFilterAlerte] = useState<string | null>(null);
  // Indicateur que les filtres viennent de l'URL (pour afficher le bandeau)
  const [isUrlFilterActive, setIsUrlFilterActive] = useState(false);

  // Lecture des query params URL pour pré-appliquer des filtres
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const alerteParam = searchParams.get('alerte');

    let hasUrlFilter = false;

    if (statusParam) {
      // Appliquer le filtre statut depuis l'URL
      setFilterStatus(statusParam);
      hasUrlFilter = true;
    }

    if (alerteParam === 'marge_critique') {
      // Activer le filtre marge critique (< 18%)
      setFilterAlerte('marge_critique');
      hasUrlFilter = true;
    } else {
      setFilterAlerte(null);
    }

    setIsUrlFilterActive(hasUrlFilter);
  }, [searchParams]);

  // Fonction pour effacer les filtres URL
  const clearUrlFilters = useCallback(() => {
    setSearchParams({});
    setFilterStatus('all');
    setFilterAlerte(null);
    setIsUrlFilterActive(false);
  }, [setSearchParams]);

  useEffect(() => {
    if (user) {
      loadEntreprise();
    }
  }, [user]);

  useEffect(() => {
    if (entrepriseId) {
      loadProjects();
    }
  }, [entrepriseId]);

  // Setup Realtime subscriptions
  const handleRealtimeChange = useCallback(() => {
    loadProjects();
  }, []);

  useChantiersRealtime(entrepriseId, handleRealtimeChange);

  // Hook centralisé pour la gestion des statuts (incluant archivage)
  const {
    changeStatus,
    archive,
    unarchive,
    canTransitionTo,
    getModalRequired,
    canArchive,
  } = useChantierStatus({
    onSuccess: () => loadProjects()
  });

  // Fonction pour vérifier la limite avant d'ouvrir le modal de création
  const handleOpenCreateDialog = useCallback(() => {
    // Vérifier si l'utilisateur peut créer un chantier
    const canCreate = canCreateChantier();

    if (!canCreate.allowed) {
      // Afficher le modal de limite atteinte
      setLimitMessage(canCreate.message || "Vous avez atteint la limite de chantiers pour votre plan.");
      setShowLimitModal(true);
      return;
    }

    // Si autorisé, ouvrir le dialog de création
    setDialogOpen(true);
  }, [canCreateChantier]);

  // Effet pour activer le highlight du nouveau chantier créé depuis la checklist
  useEffect(() => {
    if (highlightNewChantier && lastCreatedChantierId) {
      setHighlightedChantierId(lastCreatedChantierId);
      // Désactiver le flag dans le store
      setHighlightNewChantier(false);

      // Arrêter le highlight après 5 secondes
      const timeout = setTimeout(() => {
        setHighlightedChantierId(null);
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [highlightNewChantier, lastCreatedChantierId, setHighlightNewChantier]);

  // Redirection vers le premier chantier si on vient de la checklist avec une action spécifique
  useEffect(() => {
    const highlightAction = (location.state as any)?.highlightAction;
    // Si on vient de la checklist avec une action de highlight (devis, affectation, etc.)
    // ET qu'on a au moins un chantier, on redirige vers le premier chantier
    if (fromChecklist && highlightAction && projects.length > 0) {
      const firstProject = projects[0];
      navigate(`/projects/${firstProject.id}?highlight=${highlightAction}`, {
        state: { fromChecklist: true },
        replace: true,
      });
    }
  }, [fromChecklist, location.state, projects, navigate]);

  const loadEntreprise = async () => {
    try {
      const { data, error } = await supabase
        .from("entreprises")
        .select("id")
        .eq("proprietaire_user_id", user?.id)
        .single();

      if (error) throw error;
      setEntrepriseId(data.id);
    } catch (error: any) {
      console.error("Erreur chargement entreprise:", error);
    }
  };

  const loadProjects = async () => {
    try {
      const { data, error } = await supabase
        .from("chantiers")
        .select("*")
        .eq("entreprise_id", entrepriseId)
        .order("date_creation", { ascending: false });

      if (error) throw error;

      // Charger les métriques et vérifier les analyses IA pour chaque chantier
      const projectsWithMetrics = await Promise.all(
        (data || []).map(async (project) => {
          // Charger les métriques
          const { data: metricsData } = await supabase
            .from("chantier_metrics_realtime")
            .select("metrics")
            .eq("chantier_id", project.id)
            .single();

          // Vérifier si une analyse IA existe (essayer les deux tables)
          let hasAiAnalysis = false;

          // Essayer d'abord la nouvelle table analyses_ia
          const { count: newCount, error: newErr } = await supabase
            .from("analyses_ia" as any)
            .select("id", { count: 'exact', head: true })
            .eq("chantier_id", project.id);
          if (!newErr && (newCount || 0) > 0) {
            hasAiAnalysis = true;
          }

          // Si pas trouvé, essayer l'ancienne table ai_analyses
          if (!hasAiAnalysis) {
            const { count: oldCount, error: oldErr } = await supabase
              .from("ai_analyses")
              .select("id", { count: 'exact', head: true })
              .eq("chantier_id", project.id);
            if (!oldErr && (oldCount || 0) > 0) {
              hasAiAnalysis = true;
            }
          }

          // Récupérer les affectations retard avec jours_travailles pour le calcul de date effective
          const { data: allAffectations } = await supabase
            .from("affectations_chantiers")
            .select("date_debut, date_fin, notes, jours_travailles, jours_details, heures_par_jour")
            .eq("chantier_id", project.id)
            .order("date_fin", { ascending: false });

          // Filtrer les affectations de retard spécifiquement
          const delayAffectations = allAffectations?.filter(a => a.notes?.startsWith("Retard chantier")) || [];

          // Extraire heuresParJour de la première affectation normale (comme Dashboard)
          const affectationsNormales = allAffectations?.filter(a => !a.notes?.startsWith("Retard chantier")) || [];
          const heuresParJour = affectationsNormales[0]?.heures_par_jour || 8;

          // Agréger tous les jours_details de toutes les affectations (comme Dashboard)
          let allJoursDetails = allAffectations?.flatMap(a => a.jours_details || []) || [];

          // Si jours_details est vide, le générer depuis les dates des affectations
          if (allJoursDetails.length === 0 && allAffectations && allAffectations.length > 0) {
            allJoursDetails = [];
            for (const aff of allAffectations) {
              if (!aff.date_debut || !aff.date_fin) continue;
              const start = new Date(aff.date_debut);
              const end = new Date(aff.date_fin);
              let current = new Date(start);
              while (current <= end) {
                if (current.getDay() !== 0 && current.getDay() !== 6) {
                  allJoursDetails.push({
                    date: current.toISOString().split('T')[0],
                    period: 'FULL' as const
                  });
                }
                current.setDate(current.getDate() + 1);
              }
            }
          }

          // Préparer les données retard pour getDateFinEffective
          const affectationsRetardData: AffectationRetard[] = delayAffectations.map(a => ({
            date_fin: a.date_fin,
            jours_travailles: a.jours_travailles || 0,
            jours_details: a.jours_details as AffectationRetard['jours_details']
          }));

          // Charger les suspensions terminées (avec date_reprise)
          const { data: suspensionsTermineesData } = await supabase
            .from("suspensions_chantiers")
            .select("date_suspension, date_reprise, duree_suspension_jours")
            .eq("chantier_id", project.id)
            .not("date_reprise", "is", null);

          const suspensionsForCalc: SuspensionInfoDateUtils[] = (suspensionsTermineesData || []).map(s => ({
            date_suspension: s.date_suspension,
            date_reprise: s.date_reprise,
            duree_suspension_jours: s.duree_suspension_jours
          }));

          // Calculer date_fin_effective avec la source unique de vérité
          const dateFinEffectiveDate = getDateFinEffective({
            date_fin_estimee: project.date_fin_estimee,
            date_fin_initiale: project.date_fin_initiale,
            affectationsRetard: affectationsRetardData,
            suspensionsTerminees: suspensionsForCalc
          });
          const dateFinEffective = dateFinEffectiveDate
            ? format(dateFinEffectiveDate, 'yyyy-MM-dd')
            : project.date_fin_estimee;

          // Calculer les dates de retard (min date_debut, max date_fin)
          let dateDebutRetard: string | undefined;
          let dateFinRetard: string | undefined;
          let joursRetard = 0;
          if (delayAffectations.length > 0) {
            // Trier par date_debut pour avoir la première
            const sortedByDebut = [...delayAffectations].sort((a, b) => a.date_debut.localeCompare(b.date_debut));
            dateDebutRetard = sortedByDebut[0].date_debut;
            dateFinRetard = delayAffectations.reduce((max, a) =>
              a.date_fin > max ? a.date_fin : max, delayAffectations[0].date_fin);

            // Calculer le nombre de jours de retard uniques (pas par membre d'équipe)
            // IMPORTANT: Ne pas sommer jours_travailles car c'est par membre d'équipe
            // Ex: 1 jour de retard avec 3 membres = 3 affectations avec jours_travailles=1
            const uniqueDelayDays = new Set<string>();
            for (const aff of delayAffectations) {
              if (aff.jours_details && Array.isArray(aff.jours_details)) {
                for (const detail of aff.jours_details as Array<{ date: string; period: string; value: number }>) {
                  uniqueDelayDays.add(`${detail.date}:${detail.period}`);
                }
              }
            }

            if (uniqueDelayDays.size > 0) {
              // Compter: FULL = 1 jour, AM ou PM = 0.5 jour
              for (const key of uniqueDelayDays) {
                const period = key.split(':')[1];
                joursRetard += period === 'FULL' ? 1 : 0.5;
              }
            } else {
              // Fallback si pas de jours_details: prendre le max jours_travailles
              joursRetard = Math.max(...delayAffectations.map(a => a.jours_travailles || 0), 0);
            }

            // FALLBACK FINAL: Si joursRetard est trop petit par rapport aux dates,
            // calculer en jours ouvrés entre dateDebutRetard et dateFinRetard
            if (dateDebutRetard && dateFinRetard && joursRetard < 2) {
              const countBusinessDays = (start: string, end: string): number => {
                let count = 0;
                const current = new Date(start);
                const endDate = new Date(end);
                while (current <= endDate) {
                  const day = current.getDay();
                  if (day !== 0 && day !== 6) count++;
                  current.setDate(current.getDate() + 1);
                }
                return count;
              };
              const calculatedDays = countBusinessDays(dateDebutRetard, dateFinRetard);
              if (calculatedDays > joursRetard) {
                joursRetard = calculatedDays;
              }
            }
          }

          // Charger les informations de suspension (suspension active ou dernière reprise)
          let suspensionDate: string | undefined;
          let suspensionRepriseEstimee: string | undefined;
          let dateReprise: string | undefined;

          // Si le chantier est suspendu, charger la suspension active
          if (project.etat_chantier === 'suspendu') {
            const { data: suspensionActive } = await supabase
              .from('suspensions_chantiers')
              .select('date_suspension, date_reprise_estimee')
              .eq('chantier_id', project.id)
              .is('date_reprise', null)
              .order('date_suspension', { ascending: false })
              .limit(1)
              .single();

            if (suspensionActive) {
              suspensionDate = suspensionActive.date_suspension;
              suspensionRepriseEstimee = suspensionActive.date_reprise_estimee;
            }
          } else if (project.etat_chantier === 'en_cours') {
            // Si en cours, vérifier s'il y a une reprise récente (moins de 48h)
            const { data: derniereReprise } = await supabase
              .from('suspensions_chantiers')
              .select('date_suspension, date_reprise')
              .eq('chantier_id', project.id)
              .not('date_reprise', 'is', null)
              .order('date_reprise', { ascending: false })
              .limit(1)
              .single();

            if (derniereReprise?.date_reprise) {
              const dateRepriseObj = new Date(derniereReprise.date_reprise);
              const now = new Date();
              const diffHeures = (now.getTime() - dateRepriseObj.getTime()) / (1000 * 60 * 60);
              // Garder les infos si la reprise date de moins de 48h
              if (diffHeures < 48) {
                suspensionDate = derniereReprise.date_suspension;
                dateReprise = derniereReprise.date_reprise;
              }
            }
          }

          return {
            ...project,
            metrics: metricsData?.metrics as any,
            hasAiAnalysis,
            hasDelayRegistered: delayAffectations.length > 0,
            dateDebutRetard,
            dateFinRetard,
            joursRetard, // Nombre de jours ouvrés de retard (calculé correctement avec demi-journées)
            dateFinEffective, // Nouvelle propriété : date_fin la plus tardive
            suspensionDate,
            suspensionRepriseEstimee,
            dateReprise,
            heuresParJour,
            allJoursDetails,
          };
        })
      );

      setProjects(projectsWithMetrics);
    } catch (error: any) {
      console.error("Erreur chargement chantiers:", error);
    }
  };

  // Filtrer les projets selon le filtre sélectionné + filtre alerte URL
  // "all" affiche TOUS les chantiers y compris les archivés
  const filteredProjects = useMemo(() => {
    let filtered = projects.filter(p => {
      if (filterStatus === 'all') {
        // "all" = vraiment tous les chantiers (y compris archivés)
        return true;
      }
      if (filterStatus === 'archive') {
        // Uniquement les archivés
        return p.etat_chantier === 'archive';
      }
      // Filtre par statut spécifique
      return p.etat_chantier === filterStatus;
    });

    // Appliquer le filtre alerte (marge < seuil BON BTP 18%) si actif
    if (filterAlerte === 'marge_critique') {
      filtered = filtered.filter(p => {
        const margePct = p.metrics?.marge_finale_pct ?? 100;
        return margePct < SEUILS_MARGE.BON;
      });
    }

    return filtered;
  }, [projects, filterStatus, filterAlerte]);

  // Calcul des statistiques
  // Les chantiers archivés sont comptés selon leur statut AVANT archivage (etat_avant_archivage)
  const projectsNonArchives = projects.filter(p => p.etat_chantier !== 'archive');
  const totalChantiers = projects.length; // TOUS les chantiers, y compris archivés
  const chantiersEnCours = projects.filter(p =>
    p.etat_chantier === 'en_cours' ||
    (p.etat_chantier === 'archive' && p.etat_avant_archivage === 'en_cours')
  ).length;
  const chantiersTermines = projects.filter(p =>
    p.etat_chantier === 'termine' ||
    (p.etat_chantier === 'archive' && p.etat_avant_archivage === 'termine')
  ).length;
  const chantiersEnRetard = projectsNonArchives.filter(p => {
    if (!p.date_fin_estimee || p.etat_chantier === 'termine' || p.etat_chantier === 'annule') return false;
    return new Date(p.date_fin_estimee) < new Date();
  }).length;
  const chantiersArchives = projects.filter(p => p.etat_chantier === 'archive').length;

  const handleEditState = (project: any) => {
    setEditingProject(project);
    setEditStateDialogOpen(true);
  };

  // Utilise le hook centralisé useChantierStatus pour validation et synchronisation
  const handleUpdateState = async (newState: string) => {
    if (!editingProject) return;

    const currentStatus = (editingProject.etat_chantier || 'en_cours') as StatutChantier;
    const targetStatus = newState as StatutChantier;

    const result = await changeStatus(editingProject.id, currentStatus, targetStatus);

    if (result.success) {
      setEditStateDialogOpen(false);
      setEditingProject(null);
      // onSuccess (loadProjects) appelé automatiquement par le hook
    } else if (result.modalRequired) {
      setEditStateDialogOpen(false);
      setEditingProject(null);
      // Rediriger vers la page détail pour les modals complexes
      if (result.modalRequired === 'ChantierClosureDialog') {
        navigate(`/projects/${editingProject.id}?terminer=true`);
      } else {
        navigate(`/projects/${editingProject.id}`);
      }
    }
  };

  // Handler pour changer l'état directement depuis le menu du ProjectCard
  // Utilise le hook centralisé useChantierStatus pour validation et synchronisation
  const handleChangeState = async (id: string, newState: string) => {
    const project = projects.find(p => p.id === id);
    if (!project) return;

    const currentStatus = project.etat_chantier as StatutChantier;
    const targetStatus = newState as StatutChantier;

    // Utiliser le hook centralisé pour la transition
    const result = await changeStatus(id, currentStatus, targetStatus);

    // Si un modal est requis, l'ouvrir
    if (!result.success && result.modalRequired) {
      switch (result.modalRequired) {
        case 'ChantierClosureDialog':
          // Rediriger vers la page de détail avec le paramètre pour ouvrir la modal
          navigate(`/projects/${id}?terminer=true`);
          break;

        case 'ModalSuspensionChantier':
          setSelectedChantierForSuspension(project);
          setSuspensionModalOpen(true);
          break;

        case 'ModalRepriseChantier':
          // Charger la suspension active du chantier
          try {
            const { data: suspension } = await supabase
              .from('suspensions_chantiers')
              .select('*')
              .eq('chantier_id', id)
              .is('date_reprise', null)
              .order('date_suspension', { ascending: false })
              .limit(1)
              .single();

            if (suspension) {
              setSelectedChantierForSuspension(project);
              setSuspensionInfoForReprise(suspension as SuspensionInfo);
              setRepriseModalOpen(true);
            } else {
              // Si pas de suspension trouvée, remettre directement en cours
              // via le hook (transition directe suspendu → en_cours sans modal)
              const directResult = await changeStatus(id, currentStatus, 'en_cours');
              if (directResult.success) {
                toast.success(toasts.updated, { description: "Le chantier a été remis en cours." });
              }
            }
          } catch (error) {
            console.error('[Projects] Erreur chargement suspension:', error);
          }
          break;

        case 'ModalAnnulationChantier':
          // Ouvrir la modal d'annulation (si elle existe)
          // Pour l'instant, on redirige vers la page de détail
          navigate(`/projects/${id}?annuler=true`);
          break;

        case 'ModalReouvertureChantier':
        case 'ModalReactivationChantier':
          // Rediriger vers la page de détail pour ces modals
          navigate(`/projects/${id}`);
          break;

        default:
          if (import.meta.env.DEV) console.warn('[Projects] Modal non géré:', result.modalRequired);
      }
    }
    // Si success: true, le hook a déjà émis les événements et appelé onSuccess (loadProjects)
  };

  // Handler pour prolonger le retard (navigue vers Penalty Simulator en mode ajout)
  const handleProlongerRetard = (id: string) => {
    if (import.meta.env.DEV) console.log('[Projects] handleProlongerRetard called with id:', id);
    navigate(`/financial?tab=retard`, { state: { chantierId: id } });
  };

  const handleEditProject = (id: string) => {
    const project = projects.find(p => p.id === id);
    if (!project) return;
    setEditingProject(project);
    setDialogOpen(true);
  };

  const handleDeleteProject = (id: string) => {
    // Vérifier si la suppression est gratuite
    const deletionCheck = checkDeletionAllowed();

    if (deletionCheck.isFree) {
      // Suppression gratuite - afficher le warning modal avec message positif
      setIsDeletionFree(true);
      setDeleteWarningMessage(deletionCheck.message);
      setPendingDeleteId(id);
      setShowDeleteWarningModal(true);
    } else {
      // Suppression payante - afficher le warning modal avec avertissement
      setIsDeletionFree(false);
      setDeleteWarningMessage(deletionCheck.message);
      setPendingDeleteId(id);
      setShowDeleteWarningModal(true);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setIsDeletingChantier(true);

    try {
      // Supprimer le chantier (CASCADE supprimera automatiquement toutes les dépendances)
      const { error } = await supabase
        .from("chantiers")
        .delete()
        .eq("id", deleteId);

      if (error) throw error;

      // Si le chantier supprimé était celui de la checklist, nettoyer l'ID
      if (deleteId === lastCreatedChantierId) {
        setLastCreatedChantierId(null);
      }

      toast.success("Chantier supprimé", { description: "Le chantier a été supprimé définitivement." });

      loadProjects();
    } catch (error: any) {
      toast.error("Erreur", { description: error.message || toasts.errorGeneric });
    } finally {
      setIsDeletingChantier(false);
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  // Confirmer la suppression après le warning
  const confirmDeleteWithWarning = async () => {
    if (!pendingDeleteId) return;
    setIsDeletingChantier(true);

    try {
      // Si la suppression est gratuite, marquer la suppression gratuite comme utilisée
      if (isDeletionFree && subEntrepriseId) {
        await supabase.rpc('mark_free_deletion_used', {
          p_entreprise_id: subEntrepriseId,
        });
      }

      // Supprimer le chantier
      const { error } = await supabase
        .from("chantiers")
        .delete()
        .eq("id", pendingDeleteId);

      if (error) throw error;

      // Si le chantier supprimé était celui de la checklist, nettoyer l'ID
      if (pendingDeleteId === lastCreatedChantierId) {
        setLastCreatedChantierId(null);
      }

      toast.success("Chantier supprimé", {
        description: isDeletionFree
          ? "Ce chantier a été supprimé et votre suppression gratuite a été utilisée."
          : "Le chantier a été supprimé définitivement.",
      });

      // Rafraîchir les données
      refreshBillingUsage();
      loadProjects();
    } catch (error: any) {
      toast.error("Erreur", { description: error.message || toasts.errorGeneric });
    } finally {
      setIsDeletingChantier(false);
      setShowDeleteWarningModal(false);
      setPendingDeleteId(null);
    }
  };

  // Archiver un chantier (utilise le hook centralisé useChantierStatus)
  const handleArchive = async (id: string) => {
    const project = projects.find(p => p.id === id);
    if (!project) return;
    // Le hook archive() valide canBeArchived et émet les événements
    await archive(project);
    // onSuccess dans le hook appelle loadProjects()
  };

  // Désarchiver un chantier (restaure le statut d'avant archivage)
  const handleUnarchive = async (id: string) => {
    const project = projects.find(p => p.id === id);
    if (!project) return;
    // Le hook unarchive() restaure le statut et émet les événements
    await unarchive(project);
    // onSuccess dans le hook appelle loadProjects()
  };

  // Préparer les chantiers pour la sidebar (avec rentabilité)
  const chantiersForSidebar = projects.map(p => ({
    id: p.id,
    nom_chantier: p.nom_chantier,
    client: p.client,
    statut: p.statut || 'actif',
    etat_chantier: p.etat_chantier || 'en_cours',
    rentabilite: p.metrics?.marge_finale_pct || 0,
    date_debut_prevue: p.date_debut_prevue,
    date_fin_estimee: p.date_fin_estimee,
    pictogramme: p.pictogramme,
    couleur: p.couleur,
  }));

  // Handler pour la sélection depuis la sidebar
  const handleSidebarSelect = (chantierId: string) => {
    navigate(`/projects/${chantierId}`);
  };

  return (
    <>
    <div className="animate-fade-up space-y-8">
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          onConfirm={confirmDelete}
          variant="delete"
          isLoading={isDeletingChantier}
        />

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black text-gradient-primary flex items-center gap-3">
              <Building className="h-9 w-9 text-primary" aria-hidden="true" />
              {labels.nav.projects}
            </h1>
            <p className="text-muted-foreground mt-2 text-lg">
              Gérez et suivez tous vos chantiers
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Filtre par statut */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px] bg-transparent border-2 border-amber-500/70 hover:border-amber-400 hover:bg-amber-500/10 transition-colors">
                <Filter className="h-4 w-4 mr-2 text-amber-500" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="en_cours">🏗️ En cours</SelectItem>
                <SelectItem value="termine">✅ Terminé</SelectItem>
                <SelectItem value="suspendu">⏸️ Suspendu</SelectItem>
                <SelectItem value="planifie">📅 Planifié</SelectItem>
                <SelectItem value="devis_envoye">📊 Devis envoyé</SelectItem>
                <SelectItem value="brouillon">📝 Brouillon</SelectItem>
                <SelectItem value="archive">📦 Archivés ({chantiersArchives})</SelectItem>
              </SelectContent>
            </Select>
          {/* Bouton Créer avec vérification de limite */}
        <Button
          size="lg"
          className={cn(
            "gap-2 font-bold",
            isCreateProjectHighlighted && projects.length === 0 && "animate-highlight-pulse"
          )}
          disabled={!entrepriseId}
          onClick={handleOpenCreateDialog}
          aria-label={labels.actions.create}
          title={labels.actions.create}
        >
          <Plus className="h-5 w-5" aria-hidden="true" />
          {emptyStates.projects.primary}
        </Button>

        {/* Dialog centralisé de création/modification */}
        <ChantierFormDialog
          open={dialogOpen}
          onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingProject(null); }}
          project={editingProject}
          onSuccess={(chantierId) => {
            loadProjects();
            refreshBillingUsage();
            if (isCreateProjectHighlighted && chantierId) {
              setLastCreatedChantierId(chantierId);
              setHighlightNewChantier(true);
            }
          }}
          entrepriseId={entrepriseId}
        />
          </div>
      </div>

      {/* Bandeau filtre actif (si URL params) */}
      {isUrlFilterActive && (
        <div className="flex items-center justify-between gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
              Filtre actif : {' '}
              {filterAlerte === 'marge_critique' && 'Chantiers en marge critique (< 18%)'}
              {filterStatus !== 'all' && !filterAlerte && `Statut "${filterStatus}"`}
              {filterStatus !== 'all' && filterAlerte === 'marge_critique' && ` + Statut "${filterStatus}"`}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearUrlFilters}
            className="h-8 px-2 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20"
          >
            <X className="h-4 w-4 mr-1" />
            Effacer le filtre
          </Button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        <Card className="relative">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-white uppercase">
              Total Chantiers
            </CardTitle>
            <Building className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">{totalChantiers}</div>
          </CardContent>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button className="absolute bottom-3 right-3 p-1 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help" type="button">
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">Nombre total de chantiers créés dans votre entreprise, tous statuts confondus.</p>
            </HoverCardContent>
          </HoverCard>
        </Card>

        <Card className="relative">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-white uppercase">
              En Cours
            </CardTitle>
            <Clock className="h-5 w-5 text-white" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-white">{chantiersEnCours}</div>
          </CardContent>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button className="absolute bottom-3 right-3 p-1 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help" type="button">
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">Chantiers actuellement actifs avec le statut "En cours". Ce sont vos chantiers opérationnels.</p>
            </HoverCardContent>
          </HoverCard>
        </Card>

        <Card className="relative">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-white uppercase">
              Terminés
            </CardTitle>
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-green-600">{chantiersTermines}</div>
          </CardContent>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button className="absolute bottom-3 right-3 p-1 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help" type="button">
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">Chantiers clôturés avec succès. La rentabilité finale est figée à la date de clôture.</p>
            </HoverCardContent>
          </HoverCard>
        </Card>

        <Card className="relative">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-white uppercase">
              En Retard
            </CardTitle>
            <AlertCircle className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-amber-600">{chantiersEnRetard}</div>
          </CardContent>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button className="absolute bottom-3 right-3 p-1 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help" type="button">
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">Chantiers dont la date de fin estimée est dépassée mais qui ne sont pas encore terminés. Nécessitent une attention particulière.</p>
            </HoverCardContent>
          </HoverCard>
        </Card>
      </div>

      {filteredProjects.length === 0 ? (
        filterStatus === 'archive' ? (
          <EmptyState
            icon={Building}
            title="Aucun chantier archivé"
            text="Les chantiers terminés ou annulés peuvent être archivés pour ne plus apparaître dans la liste principale."
          />
        ) : projects.length === 0 ? (
        <EmptyState
          icon={Building}
          title={emptyStates.projects.title}
          text={emptyStates.projects.text}
          primaryAction={{
            label: emptyStates.projects.primary,
            onClick: handleOpenCreateDialog,
          }}
          highlightPrimaryAction={isCreateProjectHighlighted}
        />
        ) : (
          <EmptyState
            icon={Building}
            title="Aucun chantier trouvé"
            text="Aucun chantier ne correspond à ce filtre."
          />
        )
      ) : (
        <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3" data-miro-target="projects-cards-grid">
          {filteredProjects.map((project) => {
            const metrics = project.metrics || {};
            const rentabilite = metrics.marge_finale_pct || 0;
            const joursRestants = metrics.jours_restants_rentables || project.duree_estimee || 0;

            return (
              <ProjectCard
                key={project.id}
                id={project.id}
                nom_chantier={project.nom_chantier}
                client={project.client}
                rentabilite={rentabilite}
                jours_restants={joursRestants}
                budget_devis={project.budget_ht || 0}
                couts_engages={(metrics.couts_fixes_engages || 0) + (metrics.cout_main_oeuvre_reel || 0)}
                statut={project.statut || 'actif'}
                etat_chantier={project.etat_chantier}
                date_debut_prevue={project.date_debut_prevue}
                date_fin_estimee={project.dateFinEffective || project.date_fin_estimee}
                duree_estimee_jours={metrics.duree_estimee_jours || project.duree_estimee_jours}
                jours_details={project.allJoursDetails}
                heures_par_jour={project.heuresParJour}
                progress_pct={metrics.progress_pct}
                pictogramme={project.pictogramme}
                couleur={project.couleur}
                hasAiAnalysis={project.hasAiAnalysis}
                hasDelayRegistered={project.hasDelayRegistered}
                dateDebutRetard={project.dateDebutRetard}
                dateFinRetard={project.dateFinRetard}
                joursRetard={project.joursRetard}
                date_fin_initiale={project.date_fin_initiale}
                suspensionDate={project.suspensionDate}
                suspensionRepriseEstimee={project.suspensionRepriseEstimee}
                dateReprise={project.dateReprise}
                isHighlighted={highlightedChantierId === project.id}
                onEdit={handleEditProject}
                onDelete={handleDeleteProject}
                onChangeState={handleChangeState}
                onArchive={handleArchive}
                onUnarchive={handleUnarchive}
                onCloturer={(id) => handleChangeState(id, 'termine')}
                onProlongerRetard={handleProlongerRetard}
              />
            );
          })}
        </div>
      )}

      {/* Dialog d'édition de l'état */}
      <Dialog open={editStateDialogOpen} onOpenChange={setEditStateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Modifier l'état du chantier</DialogTitle>
            <DialogDescription className="text-base">
              Choisissez le nouvel état pour "{editingProject?.nom_chantier}"
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={() => handleUpdateState('brouillon')}
            >
              <span className="text-2xl mr-3">📝</span>
              <span className="font-semibold">Brouillon</span>
            </Button>
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={() => handleUpdateState('projection')}
            >
              <span className="text-2xl mr-3">🔮</span>
              <span className="font-semibold">Projection</span>
            </Button>
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={() => handleUpdateState('attente_signature')}
            >
              <span className="text-2xl mr-3">✍️</span>
              <span className="font-semibold">En attente de signature</span>
            </Button>
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={() => handleUpdateState('en_cours')}
            >
              <span className="text-2xl mr-3">🚧</span>
              <span className="font-semibold">En cours</span>
            </Button>
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={() => handleUpdateState('suspendu')}
            >
              <span className="text-2xl mr-3">⏸️</span>
              <span className="font-semibold">Suspendu</span>
            </Button>
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={() => handleUpdateState('termine')}
            >
              <span className="text-2xl mr-3">✅</span>
              <span className="font-semibold">Terminé</span>
            </Button>
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={() => handleUpdateState('annule')}
            >
              <span className="text-2xl mr-3">❌</span>
              <span className="font-semibold">Annulé</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Suspension */}
      {selectedChantierForSuspension && (
        <ModalSuspensionChantier
          isOpen={suspensionModalOpen}
          onClose={() => {
            setSuspensionModalOpen(false);
            setSelectedChantierForSuspension(null);
          }}
          chantierId={selectedChantierForSuspension.id}
          chantierNom={selectedChantierForSuspension.nom_chantier}
          onConfirm={() => {
            setSuspensionModalOpen(false);
            setSelectedChantierForSuspension(null);
            loadProjects();
          }}
        />
      )}

      {/* Modal Reprise */}
      {selectedChantierForSuspension && suspensionInfoForReprise && (
        <ModalRepriseChantier
          isOpen={repriseModalOpen}
          onClose={() => {
            setRepriseModalOpen(false);
            setSelectedChantierForSuspension(null);
            setSuspensionInfoForReprise(null);
          }}
          chantierId={selectedChantierForSuspension.id}
          chantierNom={selectedChantierForSuspension.nom_chantier}
          suspensionInfo={suspensionInfoForReprise}
          entrepriseId={entrepriseId || undefined}
          onConfirm={() => {
            setRepriseModalOpen(false);
            setSelectedChantierForSuspension(null);
            setSuspensionInfoForReprise(null);
            loadProjects();
          }}
        />
      )}

      {/* Modal Limite de chantiers atteinte */}
      <AlertDialog open={showLimitModal} onOpenChange={setShowLimitModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertCircle className="h-5 w-5" />
              Limite atteinte
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              {limitMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 space-y-3">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-2">
                Votre plan actuel : <span className="font-bold text-foreground">{planName}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Chantiers créés ce cycle : <span className="font-bold text-foreground">{billingUsage.chantiersCreatedCount}</span> / {limits.max_chantiers === -1 ? '∞' : limits.max_chantiers}
              </p>
              {billingUsage.cycleEnd && (
                <p className="text-sm text-muted-foreground mt-1">
                  Prochain renouvellement : <span className="font-medium">{new Date(billingUsage.cycleEnd).toLocaleDateString('fr-FR')}</span>
                </p>
              )}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Fermer</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowLimitModal(false);
                navigate('/settings/subscription');
              }}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              Voir les plans
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Warning Suppression */}
      <AlertDialog open={showDeleteWarningModal} onOpenChange={setShowDeleteWarningModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={cn(
              "flex items-center gap-2",
              isDeletionFree ? "text-green-500" : "text-amber-500"
            )}>
              {isDeletionFree ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              {isDeletionFree ? "Suppression gratuite disponible" : "Attention"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              {deleteWarningMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 space-y-3">
            <div className={cn(
              "rounded-lg p-4",
              isDeletionFree ? "bg-green-500/10" : "bg-amber-500/10"
            )}>
              <p className="text-sm">
                {isDeletionFree ? (
                  <>
                    <span className="font-semibold text-green-600">Bonne nouvelle !</span>
                    <br />
                    Vous pouvez supprimer ce chantier sans impact sur votre compteur mensuel.
                    C'est votre 1 suppression gratuite par cycle de facturation.
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-amber-600">Important :</span>
                    <br />
                    Même après suppression, ce chantier restera comptabilisé dans votre quota mensuel.
                    Vous avez créé {billingUsage.chantiersCreatedCount} chantier(s) ce mois, la limite est de {limits.max_chantiers === -1 ? 'illimité' : limits.max_chantiers}.
                  </>
                )}
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingChantier}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteWithWarning}
              disabled={isDeletingChantier}
              className={cn(
                isDeletionFree
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              )}
            >
              {isDeletingChantier ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Suppression...</>
              ) : (
                isDeletionFree ? "Utiliser ma suppression gratuite" : "Supprimer quand même"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>

    {/* Sidebar de sélection des chantiers - Panel fixe bord droit */}
    <ChantiersSidebarPanel
      selectedChantierId=""
      onSelectChantier={handleSidebarSelect}
      chantiers={chantiersForSidebar}
      loading={loading}
      rentabiliteLabel="Bénéfice estimé"
    />

    {/* Annotations Miro Projects */}
    <MiroAnnotations
      pageId="projects"
      annotations={[
        {
          id: "projects-cards-grid",
          targetSelector: "[data-miro-target='projects-cards-grid']",
          text: "Vue rapide de tous vos chantiers avec leur statut. Cliquez pour naviguer directement",
          position: "top",
          offsetX: 0,
          offsetY: -20,
        },
      ]}
    />
  </>
  );
};

export default Projects;
